import { useEffect, useState } from 'react';
import {
  downloadCustodianFileRendition,
  downloadCustodianIssuedCopy,
  downloadCustodianOriginal,
  getCustodianFileCopyContext,
  getCustodianPackageCopyContexts,
} from '../../api/custodianApi';
import { CustodyCopyHeader } from '../digitize/CustodyCopyHeader';
import { saveBlob } from './saveBlob';

export function labelledRenditionRefusal(error) {
  const message = typeof error === 'string' ? error : error?.message || '';
  if (/use the labelled rendition/i.test(message)) return message;
  return '';
}

function downloadName(label, fallback) {
  const safe = String(label || fallback).replace(/[\\/:*?"<>|]/g, '-').trim();
  if (!safe) return `${fallback}.pdf`;
  return safe.toLowerCase().endsWith('.pdf') ? safe : `${safe}.pdf`;
}

const CERTIFIED_COPY_CONTEXT = {
  copy_kind: 'CERTIFIED_TRUE_COPY',
  displayed_copy: 'Certified true copy',
};

/**
 * Labelled copy preview. When `issuedCopyId` is supplied the preview and
 * "Download labelled copy" load that issued (certified) file instead of the
 * package's default view rendition.
 */
export function CustodianFilePreview({ packageId, fileId, issuedCopyId = '' }) {
  const [files, setFiles] = useState([]);
  const [activeFileId, setActiveFileId] = useState(fileId || '');
  const [context, setContext] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [loading, setLoading] = useState(Boolean(packageId));
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    setActiveFileId(fileId || '');
    setFiles([]);
  }, [packageId, fileId]);

  useEffect(() => {
    if (fileId || !packageId) return undefined;
    let cancelled = false;
    getCustodianPackageCopyContexts(packageId)
      .then((contexts) => {
        if (cancelled) return;
        const list = Array.isArray(contexts) ? contexts : [];
        setFiles(list);
        const firstId = list[0]?.package_file_id ? String(list[0].package_file_id) : '';
        setActiveFileId((current) => current || firstId);
        if (!firstId) {
          setLoading(false);
          setError('No package file is available to preview.');
        }
      })
      .catch((requestError) => {
        if (cancelled) return;
        setLoading(false);
        setError(
          labelledRenditionRefusal(requestError) ||
            requestError.message ||
            'Unable to load copy context.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, [packageId, fileId]);

  useEffect(() => {
    const resolvedFileId = fileId || activeFileId;
    if (!packageId || !resolvedFileId) return undefined;

    let cancelled = false;
    let objectUrl = null;

    async function load() {
      setLoading(true);
      setError('');
      setActionError('');
      setContext(null);
      setPreviewUrl(null);
      setMimeType('');
      try {
        const nextContext = await getCustodianFileCopyContext(packageId, resolvedFileId);
        if (cancelled) return;
        setContext(
          issuedCopyId && nextContext
            ? { ...nextContext, ...CERTIFIED_COPY_CONTEXT }
            : nextContext
        );
        const result = issuedCopyId
          ? await downloadCustodianIssuedCopy(issuedCopyId)
          : await downloadCustodianFileRendition(packageId, resolvedFileId, {
              disposition: 'inline',
            });
        if (cancelled) return;
        const blob = result?.blob;
        if (!blob) {
          setError('Labelled rendition could not be loaded.');
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setPreviewUrl(objectUrl);
        setMimeType(result.mimeType || blob.type || 'application/octet-stream');
      } catch (requestError) {
        if (!cancelled) {
          setPreviewUrl(null);
          setMimeType('');
          setError(
            labelledRenditionRefusal(requestError) ||
              requestError.message ||
              'Unable to load the labelled rendition.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
      if (objectUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [packageId, fileId, activeFileId, issuedCopyId]);

  async function downloadRendition() {
    const resolvedFileId = fileId || activeFileId;
    if (!packageId || !resolvedFileId) return;
    setActionError('');
    try {
      const { blob } = issuedCopyId
        ? await downloadCustodianIssuedCopy(issuedCopyId)
        : await downloadCustodianFileRendition(packageId, resolvedFileId, {
            disposition: 'attachment',
          });
      if (!blob) {
        setActionError('Labelled rendition could not be loaded.');
        return;
      }
      saveBlob(blob, downloadName(context?.displayed_copy, 'labelled-copy'));
    } catch (requestError) {
      setActionError(
        labelledRenditionRefusal(requestError) ||
          requestError.message ||
          'Unable to download the labelled rendition.'
      );
    }
  }

  async function downloadOriginal() {
    const resolvedFileId = fileId || activeFileId;
    if (!packageId || !resolvedFileId) return;
    setActionError('');
    try {
      const { blob } = await downloadCustodianOriginal(packageId, resolvedFileId);
      if (!blob) {
        setActionError('Preserved original could not be loaded.');
        return;
      }
      saveBlob(blob, 'preserved-original');
    } catch (requestError) {
      setActionError(
        labelledRenditionRefusal(requestError) ||
          requestError.message ||
          'Unable to download the preserved original.'
      );
    }
  }

  const resolvedFileId = fileId || activeFileId;
  const showPdf = !error && mimeType === 'application/pdf' && previewUrl;

  return (
    <div className="space-y-3">
      <CustodyCopyHeader context={context} />
      {files.length > 1 ? (
        <label className="block text-xs text-evault-muted">
          Document
          <select
            className="mt-1 w-full rounded-evault border border-evault-border bg-white px-2 py-1 text-sm text-umb-neutral-dark"
            value={activeFileId}
            onChange={(event) => setActiveFileId(event.target.value)}
          >
            {files.map((file) => (
              <option key={file.package_file_id} value={file.package_file_id}>
                {file.displayed_copy || file.package_file_id}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {loading ? <p className="text-sm text-evault-muted">Loading labelled copy…</p> : null}
      {error ? (
        <p className="text-sm text-umb-error" role="alert">
          {error}
        </p>
      ) : null}
      {showPdf ? (
        <iframe
          title={context?.displayed_copy || 'Labelled copy'}
          src={previewUrl}
          className="min-h-[24rem] w-full rounded-evault border border-evault-border bg-evault-surface/40"
        />
      ) : null}
      {!loading && !error && previewUrl && mimeType !== 'application/pdf' ? (
        <p className="text-sm text-evault-muted">
          Inline preview is not available for this file type. Download the labelled copy instead.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-full bg-evault-teal-deep px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          onClick={downloadRendition}
          disabled={!resolvedFileId}
        >
          Download labelled copy
        </button>
        <button
          type="button"
          className="rounded-full border border-evault-border bg-white px-3 py-1.5 text-xs font-semibold text-evault-navy disabled:opacity-50"
          onClick={downloadOriginal}
          disabled={!resolvedFileId}
        >
          Download preserved original
        </button>
      </div>
      {actionError ? (
        <p className="text-sm text-umb-error" role="alert">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}
